import { useCallback, useEffect } from 'react';
import { Outlet, useParams, useLocation } from 'react-router-dom';

import { AxiosError } from 'axios';
import moment from 'moment-timezone';
import Hashids from 'hashids';

import { useShowData } from '@/hooks/useShowData';
import { useLanguage } from '@/contexts/LanguageContext';
import { useShowContext } from '@/contexts/ShowContext';
import GlobalHeader from '@/components/layout/GlobalHeader';
import SurveyHero from '@/components/layout/SurveyHero';
import GlobalFooter from '@/components/layout/GlobalFooter';
import '@/styles/app-shell.css';

declare global {
  interface Window {
    Optanon?: {
      ToggleInfoDisplay?: () => void;
    };
  }
}

/**
 * Locales a show offers: Canadian shows are English/French, everything else
 * English/Spanish. Shared by the header flyout, the footer modal and the
 * survey page's own locale priority logic.
 */
export const supportedLocalesFor = (isCanadian: boolean): string[] => (isCanadian ? ['en', 'fr'] : ['en', 'es']);

const Layout = () => {
  const { setShowData, setLoading, setError } = useShowContext();
  const { language, setLanguage } = useLanguage();
  const { hashed_show_id, specialist_id, timestamp } = useParams();
  const location = useLocation();

  // Skip API call if we're on the success page and have state data
  const isSuccessPage = location.pathname === '/success';
  const hasStateData = location.state?.showData;

  // Get QR timestamp
  const hasher = new Hashids('stupid scammers');
  const [ts] = hasher.decode(timestamp!);
  const qrTime = moment(Number(ts));

  // Only fetch show data if we're not on success page with state data
  const {
    showData: apiShowData,
    loading,
    error,
  } = useShowData(hashed_show_id!, specialist_id!, timestamp!, qrTime, isSuccessPage && hasStateData);

  // Use state data or API data
  const showData = isSuccessPage && hasStateData ? location.state.showData : apiShowData;

  // Update context with the appropriate data
  useEffect(() => {
    if (isSuccessPage && hasStateData) {
      setShowData(location.state.showData);
      setLoading(false);
      setError(null);
    } else {
      setShowData(apiShowData);
      setLoading(loading);
      setError(error instanceof AxiosError ? error : error ? new AxiosError(error) : null);
    }
  }, [apiShowData, loading, error, setShowData, setLoading, setError, isSuccessPage, hasStateData, location.state]);

  const openCookieSettings = useCallback(() => {
    window.Optanon?.ToggleInfoDisplay?.();
  }, []);

  if (loading || !showData) return <Outlet />;

  const brand: 'Ford' | 'Lincoln' = showData.isLincoln ? 'Lincoln' : 'Ford';
  const brandAttr = brand.toLowerCase();
  const supportedLocales = supportedLocalesFor(!!showData.isCanadian);

  return (
    // The FDS token layer resolves under `[data-brand]`; the shell carries it
    // so page chrome (fonts, colors) matches the survey. Header, hero, page
    // content and footer are SIBLINGS — the survey column owns its own
    // padding/max-width (src/styles/survey-page.css), nothing here does.
    <div className="app-shell" data-brand={brandAttr}>
      <GlobalHeader
        brand={brand}
        showLanguageChooser={supportedLocales.length > 1}
        supportedLocales={supportedLocales}
        currentLocale={language}
        onLanguageChange={setLanguage}
        // ford.com's header is a solid white bar with the blue oval at all
        // times, so Ford never starts translucent over the hero (Shannon,
        // 2026-09-10); Lincoln keeps the FDS translucent-over-hero treatment.
        hasHero={brand === 'Lincoln'}
      />
      <SurveyHero brand={brand} flush={brand === 'Ford'} />

      <main className="app-shell__main">
        <Outlet />
      </main>

      {/* Footer - only show if not Canadian */}
      {!showData.isCanadian && (
        <GlobalFooter
          brand={brand}
          supportedLanguages={supportedLocales}
          currentLocale={language}
          onLanguageChange={setLanguage}
          showLanguageSelector={supportedLocales.length > 1}
          onCookieSettings={openCookieSettings}
        />
      )}
    </div>
  );
};

export default Layout;
